/**
 * Zod Validation Schemas
 * Type-safe validation for API requests and responses
 */

import { z } from 'zod';

// Eligibility classification
export const ClassificationSchema = z.enum(['YES', 'NO', 'SOMETIMES']);

// Requirement type
export const RequirementSchema = z.enum(['LMN', 'Prescription', 'None']).optional();

// Source object
export const SourceSchema = z.object({
  label: z.string(),
  url: z.string().url(),
});

// Vertex AI Response
export const VertexResponseSchema = z.object({
  classification: ClassificationSchema,
  requires: RequirementSchema,
  confidence: z.number().min(0).max(1),
  rationale: z.string(),
  sources: z.array(SourceSchema),
  notes: z.string().optional(),
});

// Search query
export const SearchQuerySchema = z.object({
  q: z.string().min(1).max(200),
  mode: z.enum(['FSA', 'HSA']),
  random: z.boolean().optional(),
});

// Newsletter subscription
export const NewsletterSchema = z.object({
  email: z.string().email(),
  cadence: z.enum(['weekly', 'monthly']).optional(),
  categories: z.array(z.string()).optional(),
});

// User preferences
export const UserPreferencesSchema = z.object({
  cadence: z.enum(['weekly', 'monthly']),
  categories: z.array(z.string()),
});

// Community submission
export const SubmissionSchema = z.object({
  title: z.string().min(3).max(200),
  description: z.string().min(10).max(1000),
  imageUrl: z.string().url().optional(),
  productUrl: z.string().url().optional(),
  accountType: z.enum(['FSA', 'HSA', 'Both']),
  category: z.string().optional(),
  reasoning: z.string().min(10).max(500),
  sources: z.array(SourceSchema).optional(),
});

// Sponsorship request
export const SponsorshipSchema = z.object({
  companyName: z.string().min(2).max(200),
  contactName: z.string().min(2).max(100),
  email: z.string().email(),
  phone: z.string().optional(),
  companyWebsite: z.string().url(),
  productName: z.string().min(2).max(200),
  productUrl: z.string().url().optional(),
  description: z.string().min(20).max(1000),
  sponsorshipDuration: z.enum(['1month', '3months', '6months', '12months']),
  budget: z.enum(['under5k', '5k-10k', '10k-25k', '25k+']).optional(),
  goals: z.string().min(10).max(500),
  targetAudience: z.string().optional(),
});

// Item of the Day / Staff Pick
export const ShowcaseItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  image: z.string(),
  category: z.string().optional(),
  tags: z.array(z.enum(['sponsored', 'user-submitted', 'staff-pick', 'editors-pick'])).optional(),
  url: z.string(),
});

// Admin bulk import
export const BulkImportSchema = z.object({
  items: z.array(
    z.object({
      title: z.string(),
      description: z.string(),
      category: z.string().optional(),
      accountType: z.enum(['FSA', 'HSA', 'Both']),
      classification: ClassificationSchema,
      requires: RequirementSchema,
      rationale: z.string(),
      sources: z.array(SourceSchema).optional(),
    })
  ),
});

// Rate limit info
export const RateLimitSchema = z.object({
  limit: z.number(),
  remaining: z.number(),
  reset: z.number(),
});

// Open Graph image
export const OgImageSchema = z.object({
  url: z.string(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  type: z.string().nullable().optional(),
});

// Open Graph data
export const OpenGraphDataSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  image: OgImageSchema.nullable(),
  siteName: z.string().nullable(),
  url: z.string(),
  type: z.string().nullable(),
  favicon: z.string().nullable(),
});

// Open Graph response
export const OpenGraphResponseSchema = z.object({
  success: z.boolean(),
  data: OpenGraphDataSchema,
  cached: z.boolean().optional(),
  error: z.string().optional(),
});

export type Classification = z.infer<typeof ClassificationSchema>;
export type Requirement = z.infer<typeof RequirementSchema>;
export type Source = z.infer<typeof SourceSchema>;
export type VertexResponse = z.infer<typeof VertexResponseSchema>;
export type SearchQuery = z.infer<typeof SearchQuerySchema>;
export type Newsletter = z.infer<typeof NewsletterSchema>;
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;
export type Submission = z.infer<typeof SubmissionSchema>;
export type Sponsorship = z.infer<typeof SponsorshipSchema>;
export type ShowcaseItem = z.infer<typeof ShowcaseItemSchema>;
export type BulkImport = z.infer<typeof BulkImportSchema>;
export type RateLimit = z.infer<typeof RateLimitSchema>;
export type OgImage = z.infer<typeof OgImageSchema>;
export type OpenGraphData = z.infer<typeof OpenGraphDataSchema>;
export type OpenGraphResponse = z.infer<typeof OpenGraphResponseSchema>;
